import React, {
  useState,
  useEffect,
  useMemo,
} from "react";
import {
  AlertCircle,
  Target,
  Database,
  Zap,
  Info,
  Shield,
  FileText,
  ChevronDown,
  Trash2,
  Check,
  Undo2,
} from "lucide-react";
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier4Store from '../../../stores/useAtelier4Store';
import { useTranslation } from "react-i18next";
import { api } from "../../../api/apiClient";
import ctiService from "../../../services/ctiService";
import ctiResultsService from "../../../services/ctiResultsService";
import workshop3Service from "../../../services/workshop3Service"; // Use hydration service
import {
  showSuccessToast,
  showErrorToast,
  showLoadingToast,
  updateToast,
} from "../../../utils/toastUtils";

// Import components
import {
  Section,
  AttackPathCarousel,
  AssetSelector,
  AnalysisLauncher,
  PentestImportButton,
  PentestReviewModal,
  NetworkDiagramButton,
} from "./components";
import {
  savePentestPoolBatch,
  deletePentestPoolFinding,
  mergePoolIntoResults,
  poolMergeKey,
  splitPoolByState,
  isPoolConfirmed,
  isScanOrigin,
  poolToReviewState,
} from "../../../services/pentestImportService";
import CTIResultsDisplay from "../CTIDisplaySimple";
import CTIGuideModal from "./CTIGuideModal";
import WorkshopExportMenu from "../../common/WorkshopExportMenu";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { useSafeSave } from "../../../hooks/useSafeSave";
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';

/**
 * Main Threat Intelligence Page Component
 * Orchestrates the 3-step CTI analysis process:
 * 1. Select Attack Path
 * 2. Choose Support Assets
 * 3. View and Save Results
 */
const ThreatIntelligenceModern = (props) => {
  const analysisStore = useAnalysisStore();
  const atelier4Store = useAtelier4Store();
  const currentAnalysis = analysisStore.currentAnalysis;
  const ctiItemStatuses = atelier4Store.ctiItemStatuses;
  const updateCtiItemStatus = atelier4Store.updateCtiItemStatus;
  const { t } = useTranslation();

  const {
    markDataLoaded,
    markModified,
    canAutoSave,
    clearModified,
    useFormChangeDetector,
  } = useSafeSave("Atelier4ThreatIntelligence");
  const formChangeRef = useFormChangeDetector();

  // Handle silent save without toasts
  const handleSilentSave = async () => {
    if (!canAutoSave()) return "skipped";

    if (currentAnalysis?.id && ctiResults) {
      try {
        console.log(
          "ThreatIntelligence - Performing silent save of CTI results",
        );

        const allVulnerabilities =
          ctiResults.assets?.flatMap((asset) =>
            (asset.vulnerabilities || []).map((vuln) => ({
              ...vuln,
              status: itemStatuses[vuln.id] ?? (vuln.status && vuln.status !== 'non_corrige' ? vuln.status : 'non_pertinent'),
              assetId: asset.id,
              assetName: asset.name,
              attackPathId: asset.attackPathId || asset.id,
              attackPathName: asset.attackPathName,
              businessValueName: asset.businessValueName,
            })),
          ) || [];

        const allTechniques =
          ctiResults.assets?.flatMap((asset) =>
            (asset.attackTechniques || []).map((tech) => ({
              ...tech,
              status: itemStatuses[tech.id] ?? (tech.status && tech.status !== 'non_corrige' ? tech.status : 'non_pertinent'),
              assetId: asset.id,
              assetName: asset.name,
              attackPathId: asset.attackPathId || asset.id,
              attackPathName: asset.attackPathName,
              businessValueName: asset.businessValueName,
            })),
          ) || [];

        const ctiDataToSave = ctiResultsService.formatCTIDataForAttackPath(
          selectedPath,
          allVulnerabilities,
          allTechniques,
          ctiResults.assets,
          {
            analysisDate: ctiResults.analysisDate,
            overallRiskScore: ctiResults.overallRiskScore,
            totalVulnerabilities: ctiResults.totalVulnerabilities,
            totalAttackTechniques: ctiResults.totalAttackTechniques,
            dataSource: ctiResults.dataSource,
            itemStatuses: itemStatuses,
            attackPathTimestamp: attackPathTimestamps[selectedPath.id],
          },
        );

        await ctiResultsService.saveCTIResultsForAttackPath(
          currentAnalysis.id,
          selectedPath.id,
          ctiDataToSave,
        );

        // Update preloaded data cache to prevent UI collapse on re-render
        setPreloadedData((prevData) => ({
          ...prevData,
          allCtiResults: {
            ...prevData.allCtiResults,
            [selectedPath.id]: {
              ...ctiResults,
              vulnerabilities: allVulnerabilities,
              techniques: allTechniques,
              itemStatuses: itemStatuses,
            },
          },
        }));

        clearModified();
        console.log("ThreatIntelligence - Silent save successful");
        return true;
      } catch (error) {
        console.error("ThreatIntelligence - Silent save error:", error);
        return false;
      }
    }
    return false;
  };

  useSilentSaveListener('atelier4-threat-intelligence', handleSilentSave);

  // State Management
  const [isLoading, setIsLoading] = useState({
    initial: true, // for initial bulk loading
    paths: false,
    assets: false,
    analysis: false,
    ctiStatus: false,
  });
  const [error, setError] = useState(null);
  const [attackPaths, setAttackPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [supportAssets, setSupportAssets] = useState([]);
  const [ctiResults, setCtiResults] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentAnalysisType, setCurrentAnalysisType] = useState(null);
  const [ctiProgress, setCtiProgress] = useState(null); // { current, total, assetName, source, status, cacheHit }
  const [itemStatuses, setItemStatuses] = useState({});
  const [attackPathTimestamps, setAttackPathTimestamps] = useState({});
  const [ctiStatus, setCtiStatus] = useState({}); // Track CTI status for each attack path
  const [userClearedPaths, setUserClearedPaths] = useState(new Set()); // Track paths user explicitly cleared
  const [aiComponents, setAiComponents] = useState({}); // Track AI components checkbox for each attack path
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
  const [isDocxMenuOpen, setIsDocxMenuOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Pentest report import (W4 Activity 1): AI extraction -> review -> asset-level pool
  const [pentestFindings, setPentestFindings] = useState([]);
  const [pentestTechniques, setPentestTechniques] = useState([]);
  const [pentestFilename, setPentestFilename] = useState(null);
  const [pentestSource, setPentestSource] = useState(null);
  const [pentestTruncated, setPentestTruncated] = useState(false);
  const [isPentestReviewOpen, setIsPentestReviewOpen] = useState(false);
  const [pentestBanner, setPentestBanner] = useState(null);
  const [pentestPool, setPentestPool] = useState([]);
  const [pendingPoolDelete, setPendingPoolDelete] = useState(null);
  const [pentestDetectedAssets, setPentestDetectedAssets] = useState([]);
  const [pentestOrigin, setPentestOrigin] = useState('pentest');
  const [aiBusy, setAiBusy] = useState(false);
  // Tombstones: pool-derived vulns deleted from a path view must not be re-merged
  const removedPentestIds = React.useRef(new Set());

  // Preloaded data cache
  const [preloadedData, setPreloadedData] = useState({
    allCtiResults: {}, // Map of attackPathId -> CTI results
    allBusinessAssets: {}, // Map of businessValueId -> support assets
    carouselData: {}, // Additional carousel data if needed
  });

  // Ref to track when handleAnalysis just produced fresh results -
  // prevents Smart Sharing hook from overwriting them immediately
  const freshResultsPathRef = React.useRef(null);
  // Tracks for which path Smart Sharing finished resolving (pool merge must wait for it)
  const sharingResolvedRef = React.useRef(null);

  // Filter to only retained attack paths — parity with Activity 2 (OperationalScenariosAI)
  const filteredAttackPaths = useMemo(
    () => attackPaths.filter((p) => p.retained !== false && p.preliminary?.active !== true),
    [attackPaths]
  );

  // All business supports across business values (deduped) — pentest mapping is global
  const allAssetsFlat = useMemo(() => {
    const map = new Map();
    Object.values(preloadedData.allBusinessAssets || {}).forEach((list) => {
      (list || []).forEach((a) => {
        if (a && a.id && !map.has(String(a.id))) map.set(String(a.id), a);
      });
    });
    return Array.from(map.values());
  }, [preloadedData.allBusinessAssets]);

  // If selected path becomes hidden by filter (e.g. toggled to non-retained), clear selection
  useEffect(() => {
    if (selectedPath && !filteredAttackPaths.some((p) => String(p.id) === String(selectedPath.id))) {
      setSelectedPath(null);
    }
  }, [filteredAttackPaths, selectedPath]);

  // Helper function to format timestamps
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return t("common.justNow");
    if (diffMins < 60) return `${t("common.ago")} ${diffMins} min`;
    if (diffHours < 24) return `${t("common.ago")} ${diffHours}h`;
    if (diffDays < 7) return `${t("common.ago")} ${diffDays}j`;

    return date.toLocaleDateString("en", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Bulk load all data on page initialization - NOW USES SINGLE ENDPOINT
  useEffect(() => {
    if (!currentAnalysis) return;

    const loadAllDataBulk = async () => {
      setIsLoading((s) => ({ ...s, initial: true }));

      try {
        // OPTIMIZED: Single API call replaces 3 separate calls
        // Previously: attack-paths + business-values + cti-results/bulk = 3 requests
        // Now: threat-intelligence-data = 1 request
        const response = await workshop3Service.getThreatIntelligenceData(
          currentAnalysis.id,
        );

        if (response.success && response.data) {
          const {
            attackPaths,
            businessAssets,
            ctiResults,
            ctiStatus,
            timestamps,
            pentestFindings,
          } = response.data;

          // Update all state at once
          setAttackPaths(attackPaths || []);
          setPentestPool(pentestFindings || []);
          setPreloadedData({
            allCtiResults: ctiResults || {},
            allBusinessAssets: businessAssets || {},
            carouselData: {},
          });
          setCtiStatus(ctiStatus || {});
          setAttackPathTimestamps(timestamps || {});

          console.log("✅ Threat Intelligence data loaded in single request");
          markDataLoaded();
        } else {
          console.error("Failed to load threat intelligence data");
        }
      } catch (error) {
        console.error("Error loading threat intelligence data:", error);
        setError(t("workshop4.status.dataLoadingError"));
      } finally {
        setIsLoading((s) => ({ ...s, initial: false }));
      }
    };

    loadAllDataBulk();
  }, [currentAnalysis, t]);

  // NEW: Pre-calculate CTI status for ALL paths (Smart Sharing for Everyone)
  useEffect(() => {
    if (!attackPaths.length || !preloadedData.allCtiResults) return;

    console.log("[CTI Global Sync] Calculating status for all paths...");

    // 1. Build Global Asset Cache
    const globalAssetCache = {};
    Object.values(preloadedData.allCtiResults).forEach((pathData) => {
      if (!pathData?.assets) return;
      pathData.assets.forEach((asset) => {
        const assetIdStr = String(asset.id);
        if (asset.id && !globalAssetCache[assetIdStr]) {
          const fullVulns = (pathData.vulnerabilities || []).filter(
            (v) => String(v.assetId) === assetIdStr,
          );
          globalAssetCache[assetIdStr] = {
            vulnerabilitiesCount: fullVulns.length,
            techniquesCount:
              (pathData.techniques || []).filter(
                (t) => String(t.assetId) === assetIdStr,
              ).length || 0,
            exists: true,
          };
        }
      });
    });

    // 2. Map through all paths and find their total "Potential" results
    const newCtiStatus = { ...ctiStatus };
    let hasChanges = false;

    attackPaths.forEach((path) => {
      if (userClearedPaths.has(path.id)) {
        if (newCtiStatus[path.id]) {
          delete newCtiStatus[path.id];
          hasChanges = true;
        }
        return;
      }

      const allPathAssets =
        preloadedData.allBusinessAssets[path.businessValueId] || [];

      let totalVulns = 0;
      let totalTechs = 0;
      let analyzedAssets = 0;

      allPathAssets.forEach((ba) => {
        const cached = globalAssetCache[String(ba.id)];
        if (cached) {
          totalVulns += cached.vulnerabilitiesCount;
          totalTechs += cached.techniquesCount;
          analyzedAssets++;
        }
      });

      // Update status map if we found results (direct or shared)
      if (analyzedAssets > 0) {
        const current = newCtiStatus[path.id];
        if (!current || current.vulnerabilitiesCount !== totalVulns) {
          newCtiStatus[path.id] = {
            exists: true,
            vulnerabilitiesCount: totalVulns,
            techniquesCount: totalTechs,
            assetsCount: analyzedAssets,
            _shared: true,
          };
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      console.log("[CTI Global Sync] ✅ Updated status map for all paths");
      setCtiStatus(newCtiStatus);
    }
  }, [attackPaths, preloadedData, userClearedPaths]);

  useEffect(() => {
    if (!currentAnalysis || !selectedPath || isLoading.analysis) {
      // Clear results when no path is selected
      setCtiResults(null);
      setItemStatuses({});
      setSupportAssets([]);
      sharingResolvedRef.current = null;
      return;
    }

    console.log(
      `[CTI Smart Sharing] Loading data for path: ${selectedPath.referenceCode || selectedPath.id}`,
    );

    // If user explicitly deleted CTI for this path, do not auto-hydrate from smart cache
    if (userClearedPaths.has(selectedPath.id)) {
      setCtiResults(null);
      setItemStatuses({});
      sharingResolvedRef.current = String(selectedPath.id);
      return;
    }

    // If handleAnalysis just produced fresh results for this exact path, skip
    // the Smart Sharing logic to prevent it from overwriting the live results
    if (freshResultsPathRef.current === String(selectedPath.id)) {
      freshResultsPathRef.current = null; // consume the flag
      sharingResolvedRef.current = String(selectedPath.id);
      return;
    }

    // 1. Get direct CTI results for this path
    const directCtiData = preloadedData.allCtiResults[selectedPath.id];

    // 2. Get all available business assets that SHOULD be analyzed for this path
    const allBusinessAssets =
      preloadedData.allBusinessAssets[selectedPath.businessValueId] || [];

    // 3. Build a GLOBAL asset cache from ALL analyzed paths (Smart Sharing)
    const globalAssetCache = {};
    Object.entries(preloadedData.allCtiResults).forEach(
      ([pathId, pathData]) => {
        if (!pathData?.assets) return;

        pathData.assets.forEach((asset) => {
          const assetKey = String(asset.id);
          // We prefer data from the direct path, otherwise first appearance wins
          if (
            asset.id &&
            (!globalAssetCache[assetKey] ||
              String(pathId) === String(selectedPath.id))
          ) {
            const fullVulns = (pathData.vulnerabilities || []).filter(
              (v) => String(v.assetId) === assetKey,
            );
            const fullTechs = (
              pathData.techniques ||
              pathData.attackTechniques ||
              []
            ).filter((t) => String(t.assetId) === assetKey);
            globalAssetCache[assetKey] = {
              ...asset,
              vulnerabilities: fullVulns,
              attackTechniques: fullTechs,
              sourcePathId: pathId,
              sourcePathName:
                pathData.attackPath?.referenceCode ||
                pathData.attackPath?.pathName,
            };
          }
        });
      },
    );

    console.log(
      `[CTI Smart Sharing] Global cache contains ${Object.keys(globalAssetCache).length} analyzed assets`,
    );

    // 4. Resolve "Smart Shared" results for ALL business assets of this path
    const matchedAssets = [];
    const analyzedAssetIds = new Set();

    allBusinessAssets.forEach((ba) => {
      // Priority 1: Match from global cache (which includes this path's direct data)
      const cachedAsset = globalAssetCache[String(ba.id)];

      if (cachedAsset) {
        const isFromThisPath =
          String(cachedAsset.sourcePathId) === String(selectedPath.id);
        matchedAssets.push({
          ...cachedAsset, // old metadata and threat data
          ...ba, // LATEST metadata from Workshop 1 (overwrites name, description, etc)
          _fromCache: !isFromThisPath,
          _cacheSource: isFromThisPath ? null : cachedAsset.sourcePathName,
        });
        analyzedAssetIds.add(ba.id);
      } else {
        // Not analyzed anywhere yet
        matchedAssets.push({
          ...ba,
          selected: true,
          wasAnalyzed: false,
        });
      }
    });

    // 5. Build final CTI results object
    const resolvedCtiData = {
      assets: matchedAssets.filter(
        (a) =>
          (a.vulnerabilities && a.vulnerabilities.length > 0) ||
          (a.attackTechniques && a.attackTechniques.length > 0),
      ),
      vulnerabilities: matchedAssets.flatMap((a) => a.vulnerabilities || []),
      techniques: matchedAssets.flatMap((a) => a.attackTechniques || []),
      totalVulnerabilities: matchedAssets.reduce(
        (sum, a) => sum + (a.vulnerabilities?.length || 0),
        0,
      ),
      totalAttackTechniques: matchedAssets.reduce(
        (sum, a) => sum + (a.attackTechniques?.length || 0),
        0,
      ),
      attackPath: selectedPath,
      _builtFromCache: true, // Mark as built by our logic
      analysisDate: directCtiData?.analysisDate || null,
      dataSource: directCtiData?.dataSource || "Combined",
    };

    console.log(
      `[CTI Smart Sharing] Resolved ${resolvedCtiData.totalVulnerabilities} vulnerabilities for ${resolvedCtiData.assets.length} assets`,
    );

    // 6. Update State
    if (resolvedCtiData.assets.length > 0) {
      setCtiResults(resolvedCtiData);

      // Build aggregated item statuses from all loaded CTI data to support Smart Sharing
      const aggregatedStatuses = {};
      Object.values(preloadedData.allCtiResults || {}).forEach((pathData) => {
        if (pathData?.itemStatuses)
          Object.assign(aggregatedStatuses, pathData.itemStatuses);
        if (pathData?.metadata?.itemStatuses)
          Object.assign(aggregatedStatuses, pathData.metadata.itemStatuses);
      });
      // Direct data takes precedence
      if (directCtiData?.metadata?.itemStatuses)
        Object.assign(aggregatedStatuses, directCtiData.metadata.itemStatuses);
      if (directCtiData?.itemStatuses)
        Object.assign(aggregatedStatuses, directCtiData.itemStatuses);

      setItemStatuses(aggregatedStatuses);

      // If we found new data via cache that wasn't previously markers as "exists" for this path
      if (
        !ctiStatus[selectedPath.id]?.exists ||
        ctiStatus[selectedPath.id]?.vulnerabilitiesCount !==
          resolvedCtiData.totalVulnerabilities
      ) {
        setCtiStatus((prev) => ({
          ...prev,
          [selectedPath.id]: {
            exists: true,
            createdAt: directCtiData?.analysisDate || new Date().toISOString(),
            vulnerabilitiesCount: resolvedCtiData.totalVulnerabilities,
            techniquesCount: resolvedCtiData.totalAttackTechniques,
            assetsCount: resolvedCtiData.assets.length,
            _shared: true,
          },
        }));
      }
    } else {
      // Only clear results if there are no existing fresh results for this path
      // This prevents the Smart Sharing hook from wiping out results when
      // the business asset lookup fails (key mismatch or empty map)
      if (
        !ctiResults ||
        (ctiResults.attackPath?.id &&
          String(ctiResults.attackPath.id) !== String(selectedPath.id))
      ) {
        setCtiResults(null);
        setItemStatuses({});
      }
    }

    setSupportAssets(
      matchedAssets.map((a) => ({
        ...a,
        selected: true,
        wasAnalyzed: analyzedAssetIds.has(a.id),
      })),
    );
    sharingResolvedRef.current = String(selectedPath.id);
  }, [selectedPath, preloadedData, userClearedPaths]);

  // Pentest pool merge: surface asset-level pool findings in the selected path.
  // Findings attach to business supports — every path containing the asset shows them.
  useEffect(() => {
    if (!selectedPath || !currentAnalysis || isLoading.analysis) return;
    // Wait for Smart Sharing to finish resolving this path first —
    // otherwise pool-only results would replace the classic CTI results.
    if (sharingResolvedRef.current !== String(selectedPath.id)) return;
    if (!pentestPool || pentestPool.length === 0) return;
    const shells = supportAssets.length > 0
      ? supportAssets
      : (preloadedData.allBusinessAssets[selectedPath.businessValueId] || []);
    if (shells.length === 0) return;
    const eligible = splitPoolByState(pentestPool).confirmed.filter(
      (d) => !removedPentestIds.current.has(`${selectedPath.id}:${poolMergeKey(d)}`)
    );
    const assetIds = shells.map((a) => String(a.id));
    const { results, added, addedTechniques, newStatuses } = mergePoolIntoResults(
      ctiResults, eligible, assetIds, selectedPath, shells
    );
    if (added.length === 0 && addedTechniques.length === 0) return;
    setCtiResults(results);
    setItemStatuses((prev) => {
      const next = { ...prev };
      Object.entries(newStatuses).forEach(([id, st]) => {
        if (!(id in next)) next[id] = st;
      });
      return next;
    });
    const allAdded = [...added, ...addedTechniques];
    setPentestBanner({
      count: allAdded.length,
      assetsCount: new Set(allAdded.map((v) => String(v.assetId))).size,
      filename: allAdded[0]?._pentestFile || null,
    });
    markModified();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPath, pentestPool, ctiResults, supportAssets, currentAnalysis, isLoading.analysis, preloadedData]);

  // Support assets are loaded from preloaded data

  // Early return if no analysis selected
  if (!currentAnalysis) {
    return (
      <div className="bg-gray-50 min-h-screen p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t("workshop4.status.noAnalysisSelected")}
              </h2>
              <p className="text-gray-600">
                {t("workshop4.status.noAnalysisDescription")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading indicator during initial bulk data loading
  if (isLoading.initial) {
    return (
      <div className="bg-gray-50 min-h-screen p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t("workshop4.status.loadingThreatIntelligence")}
              </h2>
              <p className="text-gray-600">
                {t("workshop4.status.loadingDescription")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Event Handlers
  const handleAssetToggle = (assetId) => {
    setSupportAssets((assets) =>
      assets.map((a) =>
        a.id === assetId ? { ...a, selected: !a.selected } : a,
      ),
    );
  };

  // Toggle attack path retention for subsequent workshops (W4/W5)
  const handleToggleRetention = async (pathId, retained) => {
    if (!currentAnalysis?.id) return;
    try {
      await workshop3Service.toggleAttackPathRetention(currentAnalysis.id, pathId, retained);
      setAttackPaths(prev => prev.map(p => p.id === pathId ? { ...p, retained } : p));
      showSuccessToast(retained ? t('workshop3.activity3.retainedOn') : t('workshop3.activity3.retainedOff'));
    } catch {
      showErrorToast(t('workshop3.activity3.errors.retentionError'));
    }
  };

  const handleAIComponentsChange = (pathId, checked) => {
    setAiComponents((prev) => ({
      ...prev,
      [pathId]: checked,
    }));
  };

  const handleAnalysis = async (analysisType) => {
    const assetsToAnalyze = supportAssets.filter((a) => a.selected);
    if (assetsToAnalyze.length === 0)
      return showErrorToast(t("workshop4.messages.selectAtLeastOneAsset"));

    setIsLoading((s) => ({ ...s, analysis: true }));
    setCurrentAnalysisType(analysisType);
    setCtiProgress({ current: 0, total: assetsToAnalyze.length, assetName: assetsToAnalyze[0]?.name || '', source: analysisType, status: 'fetching', cacheHit: false });

    const analysisTypeLabels = {
      nist: t("workshop4.analysisTypes.nist"),
      euvd: t("workshop4.analysisTypes.euvd"),
      mitre: t("workshop4.analysisTypes.mitre"),
      atlas: t("workshop4.analysisTypes.atlas"),
      combined: t("workshop4.analysisTypes.combined"),
    };

    const toastId = showLoadingToast(
      `${t("common.analyzing")} ${analysisTypeLabels[analysisType]} ${t("common.for")} ${assetsToAnalyze.length} ${assetsToAnalyze.length > 1 ? t("common.assets") : t("common.asset")}...`,
    );

    try {
      const assetsWithPathInfo = assetsToAnalyze.map((asset) => ({
        ...asset,
        attackPathId: selectedPath.id,
        attackPathName: selectedPath.referenceCode || selectedPath.objectifVise,
        businessValueName: selectedPath.businessValueName,
      }));

      const newResults = await ctiService.performCTIAnalysis(
        assetsWithPathInfo,
        analysisType,
        (p) => setCtiProgress(p),
      );

      // CRITICAL FIX: Pre-process results to ensure assetId is present on all vulnerabilities and techniques
      // This handles cases where the service returns a flat list (like EUVD) without explicit assetIds
      if (newResults.assets) {
        newResults.assets.forEach((asset) => {
          if (asset.vulnerabilities) {
            asset.vulnerabilities.forEach((v) => (v.assetId = asset.id));
          }
          if (asset.attackTechniques) {
            asset.attackTechniques.forEach((t) => (t.assetId = asset.id));
          }
        });
      }

      // Ensure top-level arrays exist and are populated
      if (
        !newResults.vulnerabilities ||
        newResults.vulnerabilities.length === 0
      ) {
        const allVulns = [];
        if (newResults.assets) {
          newResults.assets.forEach((asset) => {
            if (asset.vulnerabilities) {
              allVulns.push(...asset.vulnerabilities);
            }
          });
        }
        newResults.vulnerabilities = allVulns;
      }

      if (!newResults.techniques || newResults.techniques.length === 0) {
        const allTechs = [];
        if (newResults.assets) {
          newResults.assets.forEach((asset) => {
            if (asset.attackTechniques) {
              allTechs.push(...asset.attackTechniques);
            }
          });
        }
        newResults.techniques = allTechs;
      }

      if (newResults.vulnerabilities) {
        newResults.vulnerabilities.forEach((v) => {
          if (!v.assetId) {
            // Try to find which asset this vuln belongs to by checking the assets array
            const asset = newResults.assets?.find(
              (a) =>
                a.vulnerabilities &&
                a.vulnerabilities.some((av) => av.id === v.id),
            );
            if (asset) {
              v.assetId = asset.id;
            } else if (newResults.assets?.length === 1) {
              // If there's only one asset, safe to assume it belongs to it
              v.assetId = newResults.assets[0].id;
            }
          }
        });
      }

      if (newResults.techniques) {
        newResults.techniques.forEach((t) => {
          if (!t.assetId) {
            // Try to find which asset this technique belongs to
            const asset = newResults.assets?.find(
              (a) =>
                a.attackTechniques &&
                a.attackTechniques.some((at) => at.id === t.id),
            );
            if (asset) {
              t.assetId = asset.id;
            } else if (newResults.assets?.length === 1) {
              t.assetId = newResults.assets[0].id;
            }
          }
        });
      }

      console.log("🔍 Pre-processed CTI Results:", {
        assets: newResults.assets?.length,
        totalVulns: newResults.vulnerabilities?.length,
        vulnsWithAssetId: newResults.vulnerabilities?.filter((v) => v.assetId)
          .length,
      });

      // Mark fresh results BEFORE calling setCtiResults to prevent
      // the Smart Sharing useEffect from overwriting them when preloadedData updates
      freshResultsPathRef.current = String(selectedPath.id);

      // Update results by merging with existing data
      setCtiResults((prev) => {
        if (!prev) return newResults;

        const existingAssets = prev.assets || [];
        const newAssets = newResults.assets || [];
        const updatedAssets = [...existingAssets];

        newAssets.forEach((newAsset) => {
          console.log(`🔄 Merging asset: ${newAsset.name} (${newAsset.id})`);
          const existingIndex = updatedAssets.findIndex(
            (a) => a.id === newAsset.id,
          );

          if (existingIndex >= 0) {
            console.log(`✅ Found existing asset at index ${existingIndex}`);
            const existingAsset = updatedAssets[existingIndex];
            console.log(
              `   - Existing vulns: ${existingAsset.vulnerabilities?.length || 0}`,
            );
            console.log(
              `   - New vulns: ${newAsset.vulnerabilities?.length || 0}`,
            );

            updatedAssets[existingIndex] = {
              ...existingAsset,
              ...newAsset,
              // CRITICAL: Preserve existing vulnerabilities and add new ones
              vulnerabilities: [
                ...(existingAsset.vulnerabilities || []),
                ...(newAsset.vulnerabilities || []).filter(
                  (newVuln) =>
                    !(existingAsset.vulnerabilities || []).some(
                      (existingVuln) => existingVuln.id === newVuln.id,
                    ),
                ),
              ],
              attackTechniques: [
                ...(existingAsset.attackTechniques || []),
                ...(newAsset.attackTechniques || []).filter(
                  (newTech) =>
                    !(existingAsset.attackTechniques || []).some(
                      (existingTech) => existingTech.id === newTech.id,
                    ),
                ),
              ],
            };
            console.log(
              `   - Merged vulns: ${updatedAssets[existingIndex].vulnerabilities.length}`,
            );
          } else {
            console.log(
              `⚠️ Asset not found in existing results, adding as new`,
            );
            updatedAssets.push(newAsset);
          }
        });

        // Merge top-level vulnerabilities and techniques arrays
        const existingVulnerabilities = prev.vulnerabilities || [];
        const newVulnerabilities = newResults.vulnerabilities || [];
        const mergedVulnerabilities = [
          ...existingVulnerabilities,
          ...newVulnerabilities.filter(
            (newVuln) =>
              !existingVulnerabilities.some(
                (existingVuln) => existingVuln.id === newVuln.id,
              ),
          ),
        ];

        const existingTechniques = prev.techniques || [];
        const newTechniques = newResults.techniques || [];
        const mergedTechniques = [
          ...existingTechniques,
          ...newTechniques.filter(
            (newTech) =>
              !existingTechniques.some(
                (existingTech) => existingTech.id === newTech.id,
              ),
          ),
        ];

        const updatedResults = {
          ...prev,
          ...newResults,
          assets: updatedAssets,
          vulnerabilities: mergedVulnerabilities,
          techniques: mergedTechniques,
          totalVulnerabilities: updatedAssets.reduce(
            (sum, a) => sum + (a.vulnerabilities?.length || 0),
            0,
          ),
          totalAttackTechniques: updatedAssets.reduce(
            (sum, a) => sum + (a.attackTechniques?.length || 0),
            0,
          ),
        };

        // CRITICAL: Enrich assets with full vulnerability data from merged vulnerabilities array
        // Hybrid approach:
        // 1. Start with the asset's existing vulnerabilities (from the asset merge step)
        // 2. Add any vulnerabilities from the global merged list that belong to this asset (via assetId) but are missing
        // 3. Enrich everything with full details
        const enrichedAssets = updatedAssets.map((asset) => {
          // 1. Get IDs of vulns currently attached to the asset
          const existingVulnIds = new Set(
            (asset.vulnerabilities || []).map((v) => v.id),
          );

          // 2. Find any additional vulns in the global list that belong to this asset
          const additionalVulns = mergedVulnerabilities.filter(
            (v) => v.assetId === asset.id && !existingVulnIds.has(v.id),
          );

          // 3. Combine and enrich
          const allAssetVulns = [
            ...(asset.vulnerabilities || []),
            ...additionalVulns,
          ].map((assetVuln) => {
            const fullVuln = mergedVulnerabilities.find(
              (v) => v.id === assetVuln.id,
            );
            return fullVuln ? { ...assetVuln, ...fullVuln } : assetVuln;
          });

          // Repeat for Techniques
          const existingTechIds = new Set(
            (asset.attackTechniques || []).map((t) => t.id),
          );
          const additionalTechs = mergedTechniques.filter(
            (t) => t.assetId === asset.id && !existingTechIds.has(t.id),
          );

          const allAssetTechs = [
            ...(asset.attackTechniques || []),
            ...additionalTechs,
          ].map((assetTech) => {
            const fullTech = mergedTechniques.find(
              (t) => t.id === assetTech.id,
            );
            return fullTech ? { ...assetTech, ...fullTech } : assetTech;
          });

          return {
            ...asset,
            vulnerabilities: allAssetVulns,
            attackTechniques: allAssetTechs,
          };
        });

        const finalResults = {
          ...updatedResults,
          assets: enrichedAssets,
        };

        console.log("🔍 FINAL ENRICHED RESULTS:", {
          totalVulnerabilitiesInArray: finalResults.vulnerabilities?.length,
          totalVulnerabilitiesCalculated: finalResults.totalVulnerabilities,
          assetsCount: finalResults.assets?.length,
          firstAssetVulnCount:
            finalResults.assets?.[0]?.vulnerabilities?.length,
          firstAssetName: finalResults.assets?.[0]?.name,
        });

        // Update preloaded data cache with enriched results
        setPreloadedData((prevData) => ({
          ...prevData,
          allCtiResults: {
            ...prevData.allCtiResults,
            [selectedPath.id]: finalResults,
          },
        }));

        return finalResults;
      });

      let successMessage = `${analysisTypeLabels[analysisType]} ${t("workshop4.messages.analysisCompleted")} `;
      if (analysisType === "nist" || analysisType === "euvd") {
        successMessage += `${newResults.totalVulnerabilities} ${newResults.totalVulnerabilities > 1 ? t("workshop4.messages.vulnerabilitiesFound") : t("workshop4.messages.vulnerabilityFound")}.`;
      } else if (analysisType === "mitre" || analysisType === "atlas") {
        successMessage += `${newResults.totalAttackTechniques} ${newResults.totalAttackTechniques > 1 ? t("workshop4.messages.techniquesFound") : t("workshop4.messages.techniqueFound")}.`;
      }

      updateToast(toastId, successMessage, "success");

      // Update timestamps and CTI status for this attack path
      const analysisTimestamp = new Date().toISOString();
      setAttackPathTimestamps((prev) => ({
        ...prev,
        [selectedPath.id]: analysisTimestamp,
      }));

      // Update CTI status to indicate analysis has been performed
      setCtiStatus((prev) => ({
        ...prev,
        [selectedPath.id]: {
          exists: true,
          createdAt: analysisTimestamp,
          vulnerabilitiesCount: newResults.totalVulnerabilities || 0,
          techniquesCount: newResults.totalAttackTechniques || 0,
          assetsCount: newResults.assets?.length || 0,
        },
      }));

      // Remove from userClearedPaths if they re-analyze manually
      setUserClearedPaths((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedPath.id);
        return newSet;
      });
    } catch (err) {
      updateToast(
        toastId,
        `${t("messages.analysisError")}: ${err.message}`,
        "error",
      );
    } finally {
      setIsLoading((s) => ({ ...s, analysis: false }));
      setCurrentAnalysisType(null);
      setCtiProgress(null);
    }
  };

  const handleStatusChange = (itemId, newStatus) => {
    setItemStatuses((prev) => ({
      ...prev,
      [itemId]: newStatus,
    }));
    markModified();

    // CRITICAL: Update global context immediately so other tabs (Operational Scenarios)
    // reflect the change in real-time "always updated" as requested by user
    if (updateCtiItemStatus) {
      updateCtiItemStatus(itemId, newStatus);
    }
  };

  const handlePentestExtracted = ({ findings, techniques, filename, source, truncated, droppedTechniques }) => {
    setPentestFindings(findings || []);
    setPentestTechniques(techniques || []);
    setPentestDetectedAssets([]);
    setPentestOrigin('pentest');
    setPentestFindings(findings || []);
    setPentestTechniques(techniques || []);
    setPentestFilename(filename || null);
    setPentestSource(source || filename || null);
    setPentestTruncated(!!truncated);
    setIsPentestReviewOpen(true);
    if (droppedTechniques > 0) {
      showErrorToast(t('workshop4.pentest.techniquesDropped', '{{count}} technique(s) ignorée(s) (sans ID MITRE valide).', { count: droppedTechniques }));
    }
  };

  const handleDiagramExtracted = ({ assets, findings, techniques, filename, source, droppedTechniques, droppedAssets }) => {
    setPentestDetectedAssets(assets || []);
    setPentestOrigin('diagram');
    setPentestFindings(findings || []);
    setPentestTechniques(techniques || []);
    setPentestFilename(filename || null);
    setPentestSource(source || filename || null);
    setPentestTruncated(false);
    setIsPentestReviewOpen(true);
    if (droppedAssets > 0) {
      showErrorToast(t('workshop4.networkDiagram.droppedAssets', '{{count}} bien(s) ignoré(s) (illisibles).', { count: droppedAssets }));
    }
    if (droppedTechniques > 0) {
      showErrorToast(t('workshop4.pentest.techniquesDropped', '{{count}} technique(s) ignorée(s) (sans ID MITRE valide).', { count: droppedTechniques }));
    }
  };

  // Single pool row renderer shared by the CVE and Techniques columns
  const renderPoolDocRow = (doc) => (
    <div key={doc._id} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5">
      {doc.origin === 'diagram' && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-teal-600 text-white shrink-0">
          {t('workshop4.networkDiagram.badge', 'Schéma')}
        </span>
      )}
      {doc.kind === 'technique' ? (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-600 text-white shrink-0">
          {doc.techniqueId || t('workshop4.pentest.kindTechnique', 'TECH')}
        </span>
      ) : (
        doc.cveId && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200 shrink-0">
            {doc.cveId}
          </span>
        )
      )}
      {doc.suggested && (
        <span
          title={t('workshop4.pentest.suggestedTitle', 'Suggérée par l’Assistant ACG')}
          className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0"
        >
          ★
        </span>
      )}
      {!isPoolConfirmed(doc) && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
          {t('workshop4.pentest.pendingChip', 'En attente')}
        </span>
      )}
      <span className="text-xs font-semibold text-slate-700 truncate flex-1" title={doc.title}>
        {doc.title}
      </span>
      <span className="text-xs text-slate-500 truncate">{doc.assetName}</span>
      {doc.sourceFilename && (
        <span
          className="text-[11px] text-slate-400 truncate max-w-[140px] shrink-0"
          title={t('workshop4.pentest.sourceLabel', 'Source : {{name}}', { name: doc.sourceFilename })}
        >
          {doc.sourceFilename}
        </span>
      )}
      {pendingPoolDelete === String(doc._id) ? (
        <span className="flex gap-1 shrink-0">
          <button
            onClick={() => setPendingPoolDelete(null)}
            className="text-xs px-2 py-0.5 border border-slate-300 rounded hover:bg-white"
          >
            {t('common.cancel', 'Annuler')}
          </button>
          <button
            onClick={() => handleDeletePoolFinding(doc._id)}
            className="text-xs px-2 py-0.5 bg-red-600 text-white rounded hover:bg-red-700"
          >
            {t('common.confirm', 'Confirmer')}
          </button>
        </span>
      ) : (
        <span className="flex gap-1 shrink-0 items-center">
          {!isPoolConfirmed(doc) ? (
            <button
              onClick={() => handleSetPoolState(doc._id, 'confirmed')}
              className="text-green-600 hover:text-green-800"
              title={t('workshop4.pentest.confirmPending', 'Confirmer')}
            >
              <Check size={14} />
            </button>
          ) : (
            <button
              onClick={() => handleSetPoolState(doc._id, 'pending')}
              className="text-slate-400 hover:text-slate-600"
              title={t('workshop4.pentest.unconfirm', 'Remettre en attente')}
            >
              <Undo2 size={14} />
            </button>
          )}
          <button
            onClick={() => setPendingPoolDelete(String(doc._id))}
            className="text-red-500 hover:text-red-700 shrink-0"
            title={t('workshop4.pentest.poolDelete', "Supprimer de l'historique")}
          >
            <Trash2 size={14} />
          </button>
        </span>
      )}
    </div>
  );

  const resolvePoolAsset = (finding, byId, byName, i) => {
    let asset = finding.targetAssetId && finding.targetAssetId !== '__new__'
      ? byId.get(String(finding.targetAssetId))
      : null;
    if (!asset) {
      const nm = String(finding.targetAssetName || finding.suggestedAssetName || '').toLowerCase().trim();
      asset = (nm && byName.get(nm)) || null;
    }
    return {
      assetId: asset ? String(asset.id) : `pentest-asset-${Date.now()}-${i}`,
      assetName: asset
        ? asset.name
        : (finding.targetAssetName || finding.suggestedAssetName || finding.title || finding.name),
    };
  };

  const handlePentestConfirm = async ({ findings, techniques }) => {
    const confirmed = findings || [];
    const confirmedTechs = techniques || [];
    if (!currentAnalysis || (confirmed.length === 0 && confirmedTechs.length === 0)) {
      setIsPentestReviewOpen(false);
      return;
    }
    const byId = new Map(allAssetsFlat.map((a) => [String(a.id), a]));
    const byName = new Map(
      allAssetsFlat.map((a) => [String(a.name || '').toLowerCase().trim(), a])
    );
    const toSuggestion = (s) => (s === 'corrige' ? 'corrige' : s === 'non_corrige' ? 'non_corrige' : 'inconnu');
    const docs = [
      ...confirmed.map((finding, i) => ({
        _id: finding._poolId || undefined,
        kind: 'vulnerability',
        origin: pentestOrigin === 'diagram' ? 'diagram' : 'pentest',
        state: finding.enabled === false ? 'pending' : 'confirmed',
        ...resolvePoolAsset(finding, byId, byName, i),
        title: finding.title,
        description: finding.description,
        cveId: finding.cveId || null,
        severityHint: finding.severityHint,
        statusSuggestion: toSuggestion(finding.confirmedStatus || finding.statusSuggestion),
        statusEvidence: finding.statusEvidence,
        remediation: finding.remediation,
        source: finding.source || null,
        sourceFilename: pentestSource || pentestFilename,
      })),
      ...confirmedTechs.map((tech, i) => ({
        _id: tech._poolId || undefined,
        kind: 'technique',
        origin: pentestOrigin === 'diagram' ? 'diagram' : 'pentest',
        state: tech.enabled === false ? 'pending' : 'confirmed',
        ...resolvePoolAsset(tech, byId, byName, 1000 + i),
        title: tech.name,
        description: tech.description,
        techniqueId: tech.techniqueId || null,
        tactic: tech.tactic || '',
        suggested: tech.suggested === true,
        mitreVerified: tech.mitreVerified === true,
        mitigations: Array.isArray(tech.mitigations) ? tech.mitigations : [],
        relatedFindingTitle: tech.relatedFindingTitle || '',
        relatedFindingTempId: tech.relatedFindingTempId || null,
        rationale: tech.rationale || '',
        statusSuggestion: 'inconnu',
        statusEvidence: '',
        remediation: '',
        sourceFilename: pentestSource || pentestFilename,
      })),
    ];
    try {
      const data = await savePentestPoolBatch(currentAnalysis.id, docs);
      const savedDocs = data?.findings || [];
      setPentestPool((prev) => {
        const poolKey = (d) => String(d._id || `${d.assetId}|${d.kind === 'technique' ? d.techniqueId : (d.cveId || d.title)}`);
        const byKey = new Map(prev.map((d) => [poolKey(d), d]));
        savedDocs.forEach((d) => byKey.set(String(d._id), d));
        return Array.from(byKey.values());
      });
      setIsPentestReviewOpen(false);
      const savedConfirmed = savedDocs.filter((d) => d.state !== 'pending').length;
      const savedPending = savedDocs.length - savedConfirmed;
      // Auto-check matched supports so the user flows straight into NIST/EUVD/MITRE enrich
      const matchedIds = new Set(
        [...confirmed, ...confirmedTechs]
          .map((item) => item.targetAssetId)
          .filter((id) => id && id !== '__new__')
          .map((id) => String(id))
      );
      if (matchedIds.size > 0) {
        setSupportAssets((prev) => prev.map((a) => (
          matchedIds.has(String(a.id)) ? { ...a, selected: true } : a
        )));
      }
      showSuccessToast(t('workshop4.pentest.poolSavedBoth', "{{confirmed}} confirmé(s), {{pending}} en attente — modifiables depuis l'historique.", { confirmed: savedConfirmed, pending: savedPending }));
    } catch (err) {
      showErrorToast(`${t('workshop4.pentest.poolSaveError', "Erreur lors de l'enregistrement dans l'historique")}: ${err.message}`);
    }
  };

  // Remove merged pool copies from the current path view (used by pool delete + un-confirm)
  const removePoolCopiesFromView = (poolId) => {
    setCtiResults((prev) => {
      if (!prev) return prev;
      const assets = (prev.assets || []).map((a) => ({
        ...a,
        vulnerabilities: (a.vulnerabilities || []).filter((v) => String(v._poolId) !== String(poolId)),
        attackTechniques: (a.attackTechniques || []).filter((tech) => String(tech._poolId) !== String(poolId)),
      }));
      const vulnerabilities = (prev.vulnerabilities || []).filter((v) => String(v._poolId) !== String(poolId));
      const techniques = (prev.techniques || []).filter((tech) => String(tech._poolId) !== String(poolId));
      return { ...prev, assets, vulnerabilities, techniques, totalVulnerabilities: vulnerabilities.length, totalAttackTechniques: techniques.length };
    });
  };

  const handleSetPoolState = async (poolId, state) => {
    if (!currentAnalysis) return;
    try {
      await api.put(`/analyses/${currentAnalysis.id}/pentest-findings/${poolId}`, { state });
      setPentestPool((prev) => prev.map((d) => (String(d._id) === String(poolId) ? { ...d, state } : d)));
      if (state === 'pending') removePoolCopiesFromView(poolId);
      // state === 'confirmed': the pool merge effect picks it up automatically
      markModified();
      showSuccessToast(t('workshop4.pentest.poolStateChanged', 'Statut mis à jour.'));
    } catch (err) {
      showErrorToast(`${t('workshop4.pentest.poolStateError', 'Erreur lors du changement de statut')}: ${err.message}`);
    }
  };

  const handleDeletePoolFinding = async (poolId) => {
    if (!currentAnalysis) return;
    try {
      await deletePentestPoolFinding(currentAnalysis.id, poolId);
      setPentestPool((prev) => prev.filter((d) => String(d._id) !== String(poolId)));
      removePoolCopiesFromView(poolId);
      setPendingPoolDelete(null);
      markModified();
      showSuccessToast(t('workshop4.pentest.poolDeleted', "Finding supprimé de l'historique."));
    } catch (err) {
      showErrorToast(`${t('workshop4.pentest.poolDeleteError', 'Erreur lors de la suppression')}: ${err.message}`);
    }
  };

  const handleSave = async () => {
    if (!ctiResults)
      return showErrorToast(t("workshop4.messages.noResultsToSave"));
    setIsSaving(true);

    try {
      const allVulnerabilities =
        ctiResults.assets?.flatMap((asset) =>
          (asset.vulnerabilities || []).map((vuln) => ({
            ...vuln,
            status: itemStatuses[vuln.id] ?? (vuln.status && vuln.status !== 'non_corrige' ? vuln.status : 'non_pertinent'),
            assetId: asset.id,
            assetName: asset.name,
            attackPathId: asset.attackPathId || asset.id,
            attackPathName: asset.attackPathName,
            businessValueName: asset.businessValueName,
          })),
        ) || [];

      const allTechniques =
        ctiResults.assets?.flatMap((asset) =>
          (asset.attackTechniques || []).map((tech) => ({
            ...tech,
            status: itemStatuses[tech.id] ?? (tech.status && tech.status !== 'non_corrige' ? tech.status : 'non_pertinent'),
            assetId: asset.id,
            assetName: asset.name,
            attackPathId: asset.attackPathId || asset.id,
            attackPathName: asset.attackPathName,
            businessValueName: asset.businessValueName,
          })),
        ) || [];

      // Format CTI data specifically for this attack path
      const ctiDataToSave = ctiResultsService.formatCTIDataForAttackPath(
        selectedPath,
        allVulnerabilities,
        allTechniques,
        ctiResults.assets,
        {
          analysisDate: ctiResults.analysisDate,
          overallRiskScore: ctiResults.overallRiskScore,
          totalVulnerabilities: ctiResults.totalVulnerabilities,
          totalAttackTechniques: ctiResults.totalAttackTechniques,
          dataSource: ctiResults.dataSource,
          itemStatuses: itemStatuses,
          attackPathTimestamp: attackPathTimestamps[selectedPath.id],
        },
      );

      // Save CTI results for this specific attack path
      await ctiResultsService.saveCTIResultsForAttackPath(
        currentAnalysis.id,
        selectedPath.id,
        ctiDataToSave,
      );

      // Update CTI status for this attack path
      const newStatus = {
        exists: true,
        createdAt: new Date().toISOString(),
        vulnerabilitiesCount: allVulnerabilities.length,
        techniquesCount: allTechniques.length,
        assetsCount: ctiResults.assets?.length || 0,
      };

      setCtiStatus((prev) => ({
        ...prev,
        [selectedPath.id]: newStatus,
      }));

      // Update preloaded data cache with saved results AND updated itemStatuses
      // This ensures the status changes persist when the component re-renders
      setPreloadedData((prevData) => ({
        ...prevData,
        allCtiResults: {
          ...prevData.allCtiResults,
          [selectedPath.id]: {
            ...ctiResults,
            vulnerabilities: allVulnerabilities,
            techniques: allTechniques,
            itemStatuses: itemStatuses, // Include current statuses in cache
          },
        },
      }));

      clearModified();
      showSuccessToast(t("workshop4.messages.resultsSaved"));
    } catch (err) {
      showErrorToast(`${t("workshop4.messages.saveError")}: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPath) return;
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedPath) return;

    // Clear fresh results flag so Smart Sharing hook works correctly after deletion
    freshResultsPathRef.current = null;

    // Tombstone pool-derived vulns AND techniques of this path so the pool merge does not resurrect them
    [...(ctiResults?.vulnerabilities || []), ...(ctiResults?.techniques || [])].forEach((v) => {
      if (isScanOrigin(v)) removedPentestIds.current.add(`${selectedPath.id}:${v.id}`);
    });

    setIsLoading((prev) => ({ ...prev, cti: true }));
    try {
      // Delete CTI results for this specific attack path
      await ctiResultsService.deleteCTIResultsForAttackPath(
        currentAnalysis.id,
        selectedPath.id,
      );

      // Update CTI status for this attack path
      setCtiStatus((prev) => {
        const updated = { ...prev };
        delete updated[selectedPath.id];
        return updated;
      });

      // Clear current CTI results from UI and preloaded cache
      setCtiResults(null);
      setItemStatuses({});

      // Remove from preloaded data cache
      setPreloadedData((prevData) => {
        const updatedCtiResults = { ...prevData.allCtiResults };
        delete updatedCtiResults[selectedPath.id];
        return {
          ...prevData,
          allCtiResults: updatedCtiResults,
        };
      });

      // Add the path to explicitly cleared list to prevent smart sharing from bringing it immediately back
      setUserClearedPaths((prev) => {
        const newSet = new Set(prev);
        newSet.add(selectedPath.id);
        return newSet;
      });

      showSuccessToast(t("workshop4.messages.ctiDeleted"));
    } catch (err) {
      showErrorToast(`${t("workshop4.messages.deleteError")}: ${err.message}`);
    } finally {
      setIsLoading((prev) => ({ ...prev, cti: false }));
    }
  };

  const handleDeleteAsset = async (assetId, assetName) => {
    if (!currentAnalysis) return;

    setIsLoading((prev) => ({ ...prev, cti: true }));
    try {
      // Find all paths in preloadedData that have this asset
      const affectedPaths = [];
      const updatedCtiResultsMap = { ...preloadedData.allCtiResults };
      let currentPathAffected = false;

      Object.entries(updatedCtiResultsMap).forEach(([pathId, pathData]) => {
        if (!pathData?.assets) return;

        const hasAsset = pathData.assets.some(
          (a) => String(a.id) === String(assetId),
        );
        if (hasAsset) {
          affectedPaths.push(pathId);

          // Filter out the asset and its vulnerabilities/techniques
          const newAssets = pathData.assets.filter(
            (a) => String(a.id) !== String(assetId),
          );
          const newVulns = (pathData.vulnerabilities || []).filter(
            (v) => String(v.assetId) !== String(assetId),
          );
          const newTechs = (
            pathData.techniques ||
            pathData.attackTechniques ||
            []
          ).filter((t) => String(t.assetId) !== String(assetId));

          updatedCtiResultsMap[pathId] = {
            ...pathData,
            assets: newAssets,
            vulnerabilities: newVulns,
            techniques: newTechs,
            attackTechniques: newTechs,
            totalVulnerabilities: newVulns.length,
            totalAttackTechniques: newTechs.length,
          };

          if (String(pathId) === String(selectedPath?.id)) {
            currentPathAffected = true;
          }
        }
      });

      if (affectedPaths.length === 0) {
        setIsLoading((prev) => ({ ...prev, cti: false }));
        return;
      }

      // Save to backend for each affected path
      await Promise.all(
        affectedPaths.map(async (pathId) => {
          const newData = updatedCtiResultsMap[pathId];
          if (newData.assets.length === 0) {
            // If no assets left, delete the CTI entirely for this path
            await ctiResultsService.deleteCTIResultsForAttackPath(
              currentAnalysis.id,
              pathId,
            );
            delete updatedCtiResultsMap[pathId];
            // Also clear ctiStatus
            setCtiStatus((prev) => {
              const updated = { ...prev };
              delete updated[pathId];
              return updated;
            });
          } else {
            // Update surviving data
            await ctiResultsService.saveCTIResultsForAttackPath(
              currentAnalysis.id,
              pathId,
              newData,
            );
            // Update ctiStatus counts
            setCtiStatus((prev) => ({
              ...prev,
              [pathId]: {
                ...prev[pathId],
                vulnerabilitiesCount: newData.totalVulnerabilities,
                techniquesCount: newData.totalAttackTechniques,
                assetsCount: newData.assets.length,
              },
            }));
          }
        }),
      );

      // Update local memory cache directly
      setPreloadedData((prev) => ({
        ...prev,
        allCtiResults: updatedCtiResultsMap,
      }));

      // If the current path was affected, update current view explicitly
      if (currentPathAffected && selectedPath) {
        // Set flag to prevent Smart Sharing from instantly erasing our specific state override right now
        freshResultsPathRef.current = String(selectedPath.id);

        const currentData = updatedCtiResultsMap[selectedPath.id];
        if (currentData && currentData.assets.length > 0) {
          setCtiResults(currentData);
        } else {
          setCtiResults(null);
        }
      }

      // Also uncheck the asset in the Step 2 Support Assets view
      setSupportAssets((prev) =>
        prev.map((a) =>
          String(a.id) === String(assetId) ? { ...a, selected: false } : a,
        ),
      );

      showSuccessToast(
        `Résultats CTI supprimés avec succès pour l'actif ${assetName || ""}`,
      );
    } catch (err) {
      showErrorToast(`${t("workshop4.messages.deleteError")}: ${err.message}`);
    } finally {
      setIsLoading((prev) => ({ ...prev, cti: false }));
    }
  };

  // Handle deletion of individual vulnerability or technique
  const handleDeleteItem = async (itemId, itemType, assetId) => {
    if (!currentAnalysis || !selectedPath) return;

    setIsLoading((prev) => ({ ...prev, cti: true }));
    try {
      // Get current path data
      const currentPathData = preloadedData.allCtiResults[selectedPath.id];
      if (!currentPathData) return;

      // Find the asset
      const assetIndex = currentPathData.assets.findIndex(
        (a) => String(a.id) === String(assetId)
      );
      if (assetIndex === -1) return;

      const asset = currentPathData.assets[assetIndex];
      if (itemType === 'vulnerability') {
        // Tombstone pool-derived vulns (per path) so the pool merge effect does not re-add them
        const target = (asset.vulnerabilities || []).find((v) => v.id === itemId);
        if (target && isScanOrigin(target)) removedPentestIds.current.add(`${selectedPath.id}:${itemId}`);
      }
      let updatedAsset = { ...asset };
      let updatedPathData = { ...currentPathData };

      if (itemType === 'vulnerability') {
        // Remove the vulnerability
        updatedAsset.vulnerabilities = (asset.vulnerabilities || []).filter(
          (v) => v.id !== itemId
        );
        
        // Update path vulnerabilities
        updatedPathData.vulnerabilities = (currentPathData.vulnerabilities || []).filter(
          (v) => v.id !== itemId
        );
        updatedPathData.totalVulnerabilities = updatedPathData.vulnerabilities.length;
        
        // Also update itemStatuses
        setItemStatuses((prev) => {
          const updated = { ...prev };
          delete updated[itemId];
          return updated;
        });
      } else if (itemType === 'technique') {
        // Extract technique ID from itemKey (format: tech_T1190_0)
        const techniqueId = itemId.replace(/^tech_/, '').replace(/_\d+$/, '');
        // Tombstone pool-derived techniques (per path) so the pool merge does not re-add them
        const targetTech = [...(asset.attackTechniques || []), ...(asset.techniques || [])]
          .find((tech) => (tech.id || tech.techniqueId) === techniqueId);
        if (targetTech && isScanOrigin(targetTech)) {
          removedPentestIds.current.add(`${selectedPath.id}:${techniqueId}`);
        }
        
        // Remove the technique
        updatedAsset.techniques = (asset.techniques || []).filter(
          (t, idx) => `tech_${t.id || t.techniqueId}_${idx}` !== itemId
        );
        updatedAsset.attackTechniques = (asset.attackTechniques || []).filter(
          (t, idx) => `tech_${t.id || t.techniqueId}_${idx}` !== itemId
        );
        
        // Update path techniques
        updatedPathData.techniques = (currentPathData.techniques || []).filter(
          (t, idx) => `tech_${t.id || t.techniqueId}_${idx}` !== itemId
        );
        updatedPathData.attackTechniques = updatedPathData.techniques;
        updatedPathData.totalAttackTechniques = updatedPathData.techniques.length;
        
        // Also update itemStatuses
        setItemStatuses((prev) => {
          const updated = { ...prev };
          delete updated[itemId];
          return updated;
        });
      }

      // Update asset in path
      updatedPathData.assets = [...currentPathData.assets];
      updatedPathData.assets[assetIndex] = updatedAsset;

      // Save to backend
      await ctiResultsService.saveCTIResultsForAttackPath(
        currentAnalysis.id,
        selectedPath.id,
        updatedPathData
      );

      // Update local state
      setPreloadedData((prev) => ({
        ...prev,
        allCtiResults: {
          ...prev.allCtiResults,
          [selectedPath.id]: updatedPathData
        }
      }));

      setCtiResults(updatedPathData);

      // Update ctiStatus
      setCtiStatus((prev) => ({
        ...prev,
        [selectedPath.id]: {
          ...prev[selectedPath.id],
          vulnerabilitiesCount: updatedPathData.totalVulnerabilities,
          techniquesCount: updatedPathData.totalAttackTechniques,
        },
      }));

      showSuccessToast(
        itemType === 'vulnerability'
          ? t('workshop4.messages.vulnerabilityDeleted', 'Vulnérabilité supprimée avec succès')
          : t('workshop4.messages.techniqueDeleted', 'Technique supprimée avec succès')
      );
    } catch (err) {
      showErrorToast(`${t("workshop4.messages.deleteError")}: ${err.message}`);
    } finally {
      setIsLoading((prev) => ({ ...prev, cti: false }));
    }
  };

  const handleExportPDF = async () => {
    try {
      // Import PDF generator dynamically
      const ThreatIntelligencePdfGenerator = (
        await import("../../../utils/threatIntelligencePdfGenerator")
      ).default;

      // Collect ALL CTI results from all attack paths
      const allCtiData = {
        assetThreats: {},
        summary: {
          totalAssets: 0,
          totalCVEs: 0,
          totalTechniques: 0,
        },
      };

      // FIX: Use enriched data by re-enriching all preloaded results
      // This ensures we have the full vulnerability data with severity/score
      Object.entries(preloadedData.allCtiResults).forEach(
        ([pathId, pathData]) => {
          if (pathData && pathData.assets) {
            // Get attack path info for this result
            const attackPath = attackPaths.find(
              (ap) => String(ap.id) === String(pathId),
            );
            const referenceCode =
              attackPath?.referenceCode ||
              pathData.attackPath?.referenceCode ||
              "";

            // Re-enrich the assets with full vulnerability data (same logic as when displaying)
            const fullVulnerabilities = pathData.vulnerabilities || [];
            const enrichedAssets = pathData.assets.map((asset) => {
              const assetVulns = fullVulnerabilities.filter(
                (v) => v.assetId === asset.id,
              );
              return {
                ...asset,
                vulnerabilities: assetVulns, // Full vulnerability data including severity
              };
            });

            enrichedAssets.forEach((asset) => {
              const assetKey = asset.name || asset.id;

              if (!allCtiData.assetThreats[assetKey]) {
                allCtiData.assetThreats[assetKey] = {
                  vulnerabilities: [],
                  techniques: [],
                  attackTechniques: [], // Also include as attackTechniques for consistency
                  riskScore: asset.riskScore || 0,
                  recommendations: [],
                  // Include full asset details for PDF
                  vendor: asset.vendor || "",
                  product: asset.product || "",
                  version: asset.version || "",
                  type: asset.type || "",
                  cpe: asset.cpe || "",
                  description: asset.description || "",
                  // Add attack path reference code
                  referenceCode: referenceCode,
                };
              }

              // Add vulnerabilities with full data
              if (asset.vulnerabilities) {
                console.log(
                  "🔍 PDF Export - Enriched asset vulnerabilities:",
                  asset.name,
                  asset.vulnerabilities,
                );
                const mappedVulns = asset.vulnerabilities.map((v) => {
                  const mapped = {
                    cveId: v.id || v.cveId,
                    id: v.id || v.cveId, // Also include id for consistency
                    description: v.description,
                    severity: v.severity, // Keep the full severity object
                    publishedDate: v.publishedDate,
                    lastModified: v.lastModified,
                    references: v.references || [], // Include references for PDF links
                    attackTechniques: v.mitreAttackTechniques || [],
                    status: itemStatuses[v.id] ?? (v.status && v.status !== 'non_corrige' ? v.status : 'non_pertinent'),
                  };
                  console.log("🔍 PDF Export - Mapped vulnerability:", mapped);
                  return mapped;
                });
                allCtiData.assetThreats[assetKey].vulnerabilities.push(
                  ...mappedVulns,
                );
              }

              // Add techniques (to both arrays for compatibility)
              if (asset.attackTechniques) {
                allCtiData.assetThreats[assetKey].techniques.push(
                  ...asset.attackTechniques,
                );
                allCtiData.assetThreats[assetKey].attackTechniques.push(
                  ...asset.attackTechniques,
                );
              }
            });
          }
        },
      );

      // Calculate summary
      Object.values(allCtiData.assetThreats).forEach((assetData) => {
        allCtiData.summary.totalAssets++;
        allCtiData.summary.totalCVEs += assetData.vulnerabilities?.length || 0;
        allCtiData.summary.totalTechniques += assetData.techniques?.length || 0;
      });

      const reportData = {
        ...allCtiData,
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName: currentAnalysis?.companyName || "Organisation",
      };

      const analysisData = {
        id: currentAnalysis?.id,
        name: currentAnalysis?.name,
        companyName: currentAnalysis?.companyName,
      };

      const pdfGenerator = new ThreatIntelligencePdfGenerator(
        reportData,
        analysisData,
      );
      const pdfBlob = await pdfGenerator.generateReport();

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `intelligence_menaces_${currentAnalysis?.name || "analyse"}_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccessToast("PDF exporté avec succès");
    } catch (err) {
      showErrorToast(`Erreur lors de l'exportation PDF: ${err.message}`);
    }
  };

  const handleExportPDFEn = async () => {
    try {
      // Import bilingual PDF generator (EN mode)
      const { default: ThreatIntelligencePdfGenerator } =
        await import("../../../utils/threatIntelligencePdfGenerator");

      // Collect ALL CTI results from all attack paths
      const allCtiData = {
        assetThreats: {},
        summary: {
          totalAssets: 0,
          totalCVEs: 0,
          totalTechniques: 0,
        },
      };

      // Use enriched data by re-enriching all preloaded results
      Object.entries(preloadedData.allCtiResults).forEach(
        ([pathId, pathData]) => {
          if (pathData && pathData.assets) {
            // Get attack path info for this result
            const attackPath = attackPaths.find(
              (ap) => String(ap.id) === String(pathId),
            );
            const referenceCode =
              attackPath?.referenceCode ||
              pathData.attackPath?.referenceCode ||
              "";

            // Re-enrich the assets with full vulnerability data
            const fullVulnerabilities = pathData.vulnerabilities || [];
            const enrichedAssets = pathData.assets.map((asset) => {
              const assetVulns = fullVulnerabilities.filter(
                (v) => v.assetId === asset.id,
              );
              return {
                ...asset,
                vulnerabilities: assetVulns,
              };
            });

            enrichedAssets.forEach((asset) => {
              const assetKey = asset.name || asset.id;

              if (!allCtiData.assetThreats[assetKey]) {
                allCtiData.assetThreats[assetKey] = {
                  vulnerabilities: [],
                  techniques: [],
                  attackTechniques: [],
                  riskScore: asset.riskScore || 0,
                  recommendations: [],
                  vendor: asset.vendor || "",
                  product: asset.product || "",
                  version: asset.version || "",
                  type: asset.type || "",
                  cpe: asset.cpe || "",
                  description: asset.description || "",
                  referenceCode: referenceCode,
                };
              }

              // Add vulnerabilities with full data
              if (asset.vulnerabilities) {
                const mappedVulns = asset.vulnerabilities.map((v) => ({
                  cveId: v.id || v.cveId,
                  id: v.id || v.cveId,
                  description: v.description,
                  severity: v.severity,
                  publishedDate: v.publishedDate,
                  lastModified: v.lastModified,
                  references: v.references || [],
                  attackTechniques: v.mitreAttackTechniques || [],
                  status: itemStatuses[v.id] ?? (v.status && v.status !== 'non_corrige' ? v.status : 'non_pertinent'),
                }));
                allCtiData.assetThreats[assetKey].vulnerabilities.push(
                  ...mappedVulns,
                );
              }

              // Add techniques
              if (asset.attackTechniques) {
                allCtiData.assetThreats[assetKey].techniques.push(
                  ...asset.attackTechniques,
                );
                allCtiData.assetThreats[assetKey].attackTechniques.push(
                  ...asset.attackTechniques,
                );
              }
            });
          }
        },
      );

      // Calculate summary
      Object.values(allCtiData.assetThreats).forEach((assetData) => {
        allCtiData.summary.totalAssets++;
        allCtiData.summary.totalCVEs += assetData.vulnerabilities?.length || 0;
        allCtiData.summary.totalTechniques += assetData.techniques?.length || 0;
      });

      const reportData = {
        ...allCtiData,
        analysisName: currentAnalysis?.name || "EBIOS RM Analysis",
        organizationName: currentAnalysis?.companyName || "Organization",
      };

      const analysisData = {
        id: currentAnalysis?.id,
        name: currentAnalysis?.name,
        companyName: currentAnalysis?.companyName,
      };

      const pdfGenerator = new ThreatIntelligencePdfGenerator(
        reportData,
        analysisData,
        "en",
      );
      const pdfBlob = await pdfGenerator.generateReport();

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `threat-intelligence-${currentAnalysis?.name || "analysis"}-${new Date().toISOString().split("T")[0]}-EN.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccessToast("PDF exported successfully");
    } catch (err) {
      showErrorToast(`Error exporting PDF: ${err.message}`);
    }
  };

  const handleExportDocx = async () => {
    try {
      const generateDocx = (
        await import("../../../utils/threatIntelligenceDocxGenerator")
      ).default;

      const allCtiData = {
        assetThreats: {},
        summary: {
          totalAssets: 0,
          totalCVEs: 0,
          totalTechniques: 0,
        },
      };

      // Re-enrich all preloaded results, matching handleExportPDFEn structure
      Object.entries(preloadedData.allCtiResults).forEach(
        ([pathId, pathData]) => {
          if (pathData && pathData.assets) {
            const attackPath = attackPaths.find(
              (ap) => String(ap.id) === String(pathId),
            );
            const referenceCode =
              attackPath?.referenceCode ||
              pathData.attackPath?.referenceCode ||
              "";

            const fullVulnerabilities = pathData.vulnerabilities || [];
            const enrichedAssets = pathData.assets.map((asset) => {
              const assetVulns = fullVulnerabilities.filter(
                (v) => v.assetId === asset.id,
              );
              return {
                ...asset,
                vulnerabilities: assetVulns,
              };
            });

            enrichedAssets.forEach((asset) => {
              const assetKey = asset.name || asset.id;

              if (!allCtiData.assetThreats[assetKey]) {
                allCtiData.assetThreats[assetKey] = {
                  vulnerabilities: [],
                  techniques: [],
                  attackTechniques: [],
                  riskScore: asset.riskScore || 0,
                  recommendations: [],
                  vendor: asset.vendor || "",
                  product: asset.product || "",
                  version: asset.version || "",
                  type: asset.type || "",
                  cpe: asset.cpe || "",
                  description: asset.description || "",
                  referenceCode: referenceCode,
                };
              }

              if (asset.vulnerabilities) {
                const mappedVulns = asset.vulnerabilities.map((v) => ({
                  cveId: v.id || v.cveId,
                  id: v.id || v.cveId,
                  description: v.description,
                  severity: v.severity,
                  publishedDate: v.publishedDate,
                  lastModified: v.lastModified,
                  references: v.references || [],
                  attackTechniques: v.mitreAttackTechniques || [],
                  status: itemStatuses[v.id] ?? (v.status && v.status !== 'non_corrige' ? v.status : 'non_pertinent'),
                }));
                allCtiData.assetThreats[assetKey].vulnerabilities.push(
                  ...mappedVulns,
                );
              }

              if (asset.attackTechniques) {
                allCtiData.assetThreats[assetKey].techniques.push(
                  ...asset.attackTechniques,
                );
                allCtiData.assetThreats[assetKey].attackTechniques.push(
                  ...asset.attackTechniques,
                );
              }
            });
          }
        },
      );

      Object.values(allCtiData.assetThreats).forEach((assetData) => {
        allCtiData.summary.totalAssets++;
        allCtiData.summary.totalCVEs += assetData.vulnerabilities?.length || 0;
        allCtiData.summary.totalTechniques += assetData.techniques?.length || 0;
      });

      const enhancedAssetThreats = { ...allCtiData.assetThreats };

      const reportData = {
        summary: allCtiData.summary,
        assetThreats: enhancedAssetThreats,
        analysisName: currentAnalysis?.name,
        organizationName: currentAnalysis?.companyName,
      };

      const analysisData = {
        id: currentAnalysis?.id,
        name: currentAnalysis?.name,
        companyName: currentAnalysis?.companyName,
      };

      await generateDocx(reportData, analysisData);
      showSuccessToast("DOCX exporté avec succès");
    } catch (err) {
      showErrorToast(`Erreur lors de l'exportation DOCX: ${err.message}`);
    }
  };

  const handleExportDocxEn = async () => {
    try {
      // Import bilingual DOCX generator (EN mode)
      const { default: generateThreatIntelligenceDocx } =
        await import("../../../utils/threatIntelligenceDocxGenerator");

      const allCtiData = {
        assetThreats: {},
        summary: {
          totalAssets: 0,
          totalCVEs: 0,
          totalTechniques: 0,
        },
      };

      Object.entries(preloadedData.allCtiResults).forEach(
        ([pathId, pathData]) => {
          if (pathData && pathData.assets) {
            const attackPath = attackPaths.find(
              (ap) => String(ap.id) === String(pathId),
            );
            const referenceCode =
              attackPath?.referenceCode ||
              pathData.attackPath?.referenceCode ||
              "";

            const fullVulnerabilities = pathData.vulnerabilities || [];
            const enrichedAssets = pathData.assets.map((asset) => {
              const assetVulns = fullVulnerabilities.filter(
                (v) => v.assetId === asset.id,
              );
              return {
                ...asset,
                vulnerabilities: assetVulns,
              };
            });

            enrichedAssets.forEach((asset) => {
              const assetKey = asset.name || asset.id;

              if (!allCtiData.assetThreats[assetKey]) {
                allCtiData.assetThreats[assetKey] = {
                  vulnerabilities: [],
                  techniques: [],
                  attackTechniques: [],
                  riskScore: asset.riskScore || 0,
                  recommendations: [],
                  vendor: asset.vendor || "",
                  product: asset.product || "",
                  version: asset.version || "",
                  type: asset.type || "",
                  cpe: asset.cpe || "",
                  description: asset.description || "",
                  referenceCode: referenceCode,
                };
              }

              if (asset.vulnerabilities) {
                const mappedVulns = asset.vulnerabilities.map((v) => ({
                  cveId: v.id || v.cveId,
                  id: v.id || v.cveId,
                  description: v.description,
                  severity: v.severity,
                  publishedDate: v.publishedDate,
                  lastModified: v.lastModified,
                  references: v.references || [],
                  attackTechniques: v.mitreAttackTechniques || [],
                  status: itemStatuses[v.id] ?? (v.status && v.status !== 'non_corrige' ? v.status : 'non_pertinent'),
                }));
                allCtiData.assetThreats[assetKey].vulnerabilities.push(
                  ...mappedVulns,
                );
              }

              if (asset.attackTechniques) {
                allCtiData.assetThreats[assetKey].techniques.push(
                  ...asset.attackTechniques,
                );
                allCtiData.assetThreats[assetKey].attackTechniques.push(
                  ...asset.attackTechniques,
                );
              }
            });
          }
        },
      );

      Object.values(allCtiData.assetThreats).forEach((assetData) => {
        allCtiData.summary.totalAssets++;
        allCtiData.summary.totalCVEs += assetData.vulnerabilities?.length || 0;
        allCtiData.summary.totalTechniques += assetData.techniques?.length || 0;
      });

      const reportData = {
        summary: allCtiData.summary,
        assetThreats: allCtiData.assetThreats,
        analysisName: currentAnalysis?.name,
        organizationName: currentAnalysis?.companyName,
      };

      const analysisData = {
        id: currentAnalysis?.id,
        name: currentAnalysis?.name,
        companyName: currentAnalysis?.companyName,
      };

      await generateThreatIntelligenceDocx(reportData, analysisData, "en");
      showSuccessToast("DOCX exported successfully");
    } catch (err) {
      showErrorToast(`Error exporting DOCX: ${err.message}`);
    }
  };

  const handleExportCsvFr = async () => {
    try {
      const ThreatIntelligenceCsvGenerator = (
        await import("../../../utils/threatIntelligenceCsvGenerator")
      ).default;

      const allCtiData = { assetThreats: {} };

      Object.entries(preloadedData.allCtiResults).forEach(
        ([pathId, pathData]) => {
          if (pathData && pathData.assets) {
            const attackPath = attackPaths.find(
              (ap) => String(ap.id) === String(pathId),
            );
            const referenceCode =
              attackPath?.referenceCode ||
              pathData.attackPath?.referenceCode ||
              "";

            const fullVulnerabilities = pathData.vulnerabilities || [];
            const enrichedAssets = pathData.assets.map((asset) => {
              const assetVulns = fullVulnerabilities.filter(
                (v) => v.assetId === asset.id,
              );
              return { ...asset, vulnerabilities: assetVulns };
            });

            enrichedAssets.forEach((asset) => {
              const assetKey = asset.name || asset.id;
              if (!allCtiData.assetThreats[assetKey]) {
                allCtiData.assetThreats[assetKey] = {
                  vulnerabilities: [],
                  vendor: asset.vendor || "",
                  product: asset.product || "",
                  version: asset.version || "",
                  type: asset.type || "",
                  referenceCode,
                };
              }
              const assetVulns = fullVulnerabilities.filter(
                (v) => v.assetId === asset.id,
              );
              allCtiData.assetThreats[assetKey].vulnerabilities.push(
                ...assetVulns,
              );
            });
          }
        },
      );

      const reportData = { assetThreats: allCtiData.assetThreats };
      const generator = new ThreatIntelligenceCsvGenerator(reportData, "fr");
      generator.download(
        `EBIOS-RM_Atelier4_Activite1_${currentAnalysis?.companyName || "Organisation"}_${new Date().toISOString().split("T")[0]}.xls`,
      );
      showSuccessToast("Export CSV (FR) généré avec succès");
    } catch (err) {
      console.error("Error exporting CSV:", err);
      showErrorToast(`Erreur lors de l'exportation CSV: ${err.message}`);
    }
  };

  const handleExportCsvEn = async () => {
    try {
      const ThreatIntelligenceCsvGenerator = (
        await import("../../../utils/threatIntelligenceCsvGenerator")
      ).default;

      const allCtiData = { assetThreats: {} };

      Object.entries(preloadedData.allCtiResults).forEach(
        ([pathId, pathData]) => {
          if (pathData && pathData.assets) {
            const attackPath = attackPaths.find(
              (ap) => String(ap.id) === String(pathId),
            );
            const referenceCode =
              attackPath?.referenceCode ||
              pathData.attackPath?.referenceCode ||
              "";

            const fullVulnerabilities = pathData.vulnerabilities || [];
            const enrichedAssets = pathData.assets.map((asset) => {
              const assetVulns = fullVulnerabilities.filter(
                (v) => v.assetId === asset.id,
              );
              return { ...asset, vulnerabilities: assetVulns };
            });

            enrichedAssets.forEach((asset) => {
              const assetKey = asset.name || asset.id;
              if (!allCtiData.assetThreats[assetKey]) {
                allCtiData.assetThreats[assetKey] = {
                  vulnerabilities: [],
                  vendor: asset.vendor || "",
                  product: asset.product || "",
                  version: asset.version || "",
                  type: asset.type || "",
                  referenceCode,
                };
              }
              const assetVulns = fullVulnerabilities.filter(
                (v) => v.assetId === asset.id,
              );
              allCtiData.assetThreats[assetKey].vulnerabilities.push(
                ...assetVulns,
              );
            });
          }
        },
      );

      const reportData = { assetThreats: allCtiData.assetThreats };
      const generator = new ThreatIntelligenceCsvGenerator(reportData, "en");
      generator.download(
        `EBIOS-RM_Workshop4_Activity1_${currentAnalysis?.companyName || "Organization"}_${new Date().toISOString().split("T")[0]}.xls`,
      );
      showSuccessToast("CSV Export (EN) generated successfully");
    } catch (err) {
      console.error("Error exporting CSV:", err);
      showErrorToast(`Error exporting CSV: ${err.message}`);
    }
  };

  return (
    <div
      ref={formChangeRef}
      className="bg-gray-50 min-h-screen p-4 sm:p-6 lg:p-8"
    >
      <div className="max-w-6xl mx-auto space-y-8">
        {/* === HEADER === */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {/* Left Side: Breadcrumb & Title */}
            <div>
              <div className="flex items-center text-sm text-slate-500 mb-2">
                <span className="hover:text-blue-600 transition-colors">
                  {t("workshop4.breadcrumb.workshop4")}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mx-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="font-medium text-blue-600">
                  {t("workshop4.breadcrumb.threatIntelligence")}
                </span>
              </div>
              <div className="flex items-center">
                <div className="bg-blue-100 p-2 rounded-lg mr-4">
                  <Shield size={24} className="text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">
                    {t(
                      "workshop4.navigation.threatIntelligence",
                      "Intelligence des menaces",
                    )}
                  </h1>
                  <p className="text-sm text-slate-600 mt-1">
                    {t(
                      "workshop4.attackPathSection.subtitle",
                      "Analysez vos chemins d'attaque avec les données CTI",
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Side: Export PDF & Help Buttons */}
            <div className="flex items-center gap-3">
              <WorkshopExportMenu
                onExportPdfFr={handleExportPDF}
                onExportPdfEn={handleExportPDFEn}
                onExportDocxFr={handleExportDocx}
                onExportDocxEn={handleExportDocxEn}
                onExportCsvFr={handleExportCsvFr}
                onExportCsvEn={handleExportCsvEn}
                isExporting={currentAnalysis === null}
              />
              <button
                className="text-sm font-medium bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center shadow-sm transition duration-200"
                onClick={() => setIsGuideOpen(true)}
              >
                <Info size={16} className="mr-2" />
                {t("common.help")}
              </button>
            </div>
          </div>
        </div>
        {/* === END HEADER === */}

        {/* Guide Modal */}
        {isGuideOpen && (
          <CTIGuideModal
            isOpen={isGuideOpen}
            onClose={() => setIsGuideOpen(false)}
          />
        )}
        {error && (
          <div
            className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md"
            role="alert"
          >
            <p>{error}</p>
          </div>
        )}

        {/* Scan report import (3/4) + network diagram analysis (1/4) — separate options */}
        <div className="grid gap-4 lg:grid-cols-4">
          <div className="lg:col-span-3 [&>div]:mt-0">
            <PentestImportButton
              analysisId={currentAnalysis?.id}
              businessAssets={allAssetsFlat}
              disabled={isLoading.analysis || aiBusy}
              onBusyChange={setAiBusy}
              onExtracted={handlePentestExtracted}
            />
          </div>
          <div className="lg:col-span-1">
            <NetworkDiagramButton
              analysisId={currentAnalysis?.id}
              businessAssets={allAssetsFlat}
              disabled={isLoading.analysis || aiBusy}
              onBusyChange={setAiBusy}
              onExtracted={handleDiagramExtracted}
            />
          </div>
        </div>
        {pentestPool.length > 0 && (
          <div className="bg-white rounded-xl border border-purple-200 p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="font-semibold text-slate-800">
                {t('workshop4.pentest.poolTitle', 'Historique des résultats')}
              </p>
              <span className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const state = poolToReviewState(pentestPool);
                    setPentestFindings(state.findings);
                    setPentestTechniques(state.techniques);
                    setPentestFilename(t('workshop4.pentest.poolReviewFilename', 'Historique des résultats'));
                    setPentestTruncated(false);
                    setIsPentestReviewOpen(true);
                  }}
                  className="text-xs font-medium px-2.5 py-1 rounded-lg bg-white border border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  {t('workshop4.pentest.reopenReview', 'Revoir')}
                </button>
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                  {t('workshop4.pentest.poolCount', '{{count}} finding(s)', { count: pentestPool.length })}
                </span>
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              {t('workshop4.pentest.poolSubtitle', "Findings rattachés aux biens supports. Ils apparaissent automatiquement dans les chemins d'attaque concernés.")}
              {splitPoolByState(pentestPool).pending.length > 0 && (
                <span className="block mt-0.5 text-amber-700">
                  {t('workshop4.pentest.poolPendingNote', '{{count}} en attente — invisibles dans les chemins tant que non confirmés.', { count: splitPoolByState(pentestPool).pending.length })}
                </span>
              )}
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-700">
                    {t('workshop4.pentest.poolVulnsTitle', 'CVE / Risques')}
                  </p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                    {t('workshop4.pentest.poolCount', '{{count}} finding(s)', { count: pentestPool.filter((d) => d.kind !== 'technique').length })}
                  </span>
                </div>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {pentestPool.filter((d) => d.kind !== 'technique').map((doc) => renderPoolDocRow(doc))}
                  {pentestPool.filter((d) => d.kind !== 'technique').length === 0 && (
                    <p className="text-xs text-slate-400 italic py-2 text-center">
                      {t('workshop4.pentest.poolEmptyVulns', 'Aucune CVE')}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-700">
                    {t('workshop4.pentest.poolTechsTitle', 'Techniques')}
                  </p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                    {t('workshop4.pentest.poolCount', '{{count}} finding(s)', { count: pentestPool.filter((d) => d.kind === 'technique').length })}
                  </span>
                </div>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {pentestPool.filter((d) => d.kind === 'technique').map((doc) => renderPoolDocRow(doc))}
                  {pentestPool.filter((d) => d.kind === 'technique').length === 0 && (
                    <p className="text-xs text-slate-400 italic py-2 text-center">
                      {t('workshop4.pentest.poolEmptyTechs', 'Aucune technique')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <Section
          step={1}
          icon={<Target size={24} />}
          title={t("workshop4.steps.selectAttackPath.title")}
        >
          <AttackPathCarousel
            attackPaths={filteredAttackPaths}
            selectedPath={selectedPath}
            onSelect={setSelectedPath}
            isLoading={isLoading.initial}
            timestamps={attackPathTimestamps}
            formatTimestamp={formatTimestamp}
            ctiStatus={ctiStatus}
            aiComponents={aiComponents}
            onAIComponentsChange={handleAIComponentsChange}
            onToggleRetention={handleToggleRetention}
            businessAssetsMap={preloadedData.allBusinessAssets}
          />
        </Section>

        {selectedPath && (
          <Section
            step={2}
            icon={<Database size={24} />}
            title={t("workshop4.steps.chooseAssets.title")}
          >
            <AssetSelector
              assets={supportAssets}
              onToggle={handleAssetToggle}
              isLoading={false}
            />
            {pentestBanner && (
              <div className="mt-4 bg-purple-50 border border-purple-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <p className="text-sm text-purple-900">
                  {t('workshop4.pentest.enrichBanner', '{{count}} finding(s) pentest rattaché(s) à {{assets}} actif(s){{file}}. Enrichissez maintenant ?', { count: pentestBanner.count, assets: pentestBanner.assetsCount, file: pentestBanner.filename ? ` (${pentestBanner.filename})` : '' })}
                </p>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleAnalysis('nist')}
                    disabled={isLoading.analysis}
                    className="text-sm font-medium bg-white border border-purple-300 text-purple-700 px-3 py-1.5 rounded-lg hover:bg-purple-100 disabled:opacity-50"
                  >
                    {t('workshop4.pentest.enrichNist', 'Enrichir NIST')}
                  </button>
                  <button
                    onClick={() => handleAnalysis('euvd')}
                    disabled={isLoading.analysis}
                    className="text-sm font-medium bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {t('workshop4.pentest.enrichEuvd', 'Enrichir EUVD')}
                  </button>
                  <button
                    onClick={() => setPentestBanner(null)}
                    className="text-sm text-purple-500 hover:text-purple-700 px-2"
                  >
                    {t('common.dismiss', 'Ignorer')}
                  </button>
                </div>
              </div>
            )}
            <AnalysisLauncher
              onAnalyze={handleAnalysis}
              disabled={supportAssets.filter((a) => a.selected).length === 0}
              isLoading={isLoading.analysis}
              analysisType={currentAnalysisType}
              showAtlas={aiComponents[selectedPath?.id] || false}
              progress={ctiProgress}
            />
          </Section>
        )}

        {ctiResults && (
          <Section
            step={3}
            icon={<Zap size={24} />}
            title={t("workshop4.steps.viewResults.title")}
          >
            <CTIResultsDisplay
              results={ctiResults}
              onSave={handleSave}
              onDelete={handleDelete}
              onDeleteAsset={handleDeleteAsset}
              onDeleteItem={handleDeleteItem}
              isSaving={isSaving}
              itemStatuses={itemStatuses}
              onStatusChange={handleStatusChange}
              attackPathName={selectedPath?.name}
            />
          </Section>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title={t("common.delete", "Supprimer")}
        message={t("workshop4.messages.confirmDelete", {
          pathName: selectedPath?.name || "",
        })}
        itemName={selectedPath?.name}
        itemType="attackPath"
      />
      <PentestReviewModal
        isOpen={isPentestReviewOpen}
        findings={pentestFindings}
        techniques={pentestTechniques}
        detectedAssets={pentestDetectedAssets}
        businessAssets={allAssetsFlat}
        filename={pentestFilename}
        truncated={pentestTruncated}
        onClose={() => setIsPentestReviewOpen(false)}
        onConfirm={handlePentestConfirm}
      />
    </div>
  );
;
};

export default ThreatIntelligenceModern;
